
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import Button from 'components/Button'
import Dialog, { ConfirmDialog } from 'components/Dialog'
import TableSolicitud from './components/TableSolicitud'
import ViewSolicitud from './components/ViewSolicitud'

import SolicitudService from 'services/solicitud.service'
import { setHttpMessage } from 'store/messageSlice'
import FormDeny from './components/FormDeny'
import { buildFilters, formatDate } from 'utils/utils'
import TitlePage from 'components/TitlePage'
import useFilters from 'hooks/useFilters'
import { initialPagination } from 'data/const'

/** @module Pages/Solicitud/SolicitudDAFIndex */

const currentYear = formatDate(new Date(), 'Y')

// Colores de filas de la tabla por estado.
const colorsEstado = {
    A: 'bg-orange-50',
    M: 'bg-green-50',
    P: 'bg-blue-50',
}

/**
 * Página, Lista de solicitudes de DAF.
 * @returns {JSX.Element} Retorna el componente SolicitudDAFIndex.
 */
function SolicitudDAFIndex() {
    const dispatch = useDispatch()
    const [solicitudes, setSolicitudes] = useState([])
    const [pagination, setPagination] = useState(initialPagination)
    const [selected, setSelected] = useState(null)
    const {filters, reloadFilters, ...filterHandlers} = useFilters()
    const [show, setShow] = useState({info: false, deny: false, verify: false})
    const [pendientes, setPendientes] = useState(false)
    const tableRef = useRef()
    const formRef = useRef()

    useEffect(() => {
        const loadSolicitudes = async () => {
            const {filter, ..._filters} = filters
            const _filter = {
                ...filter,
                ...buildFilters(
                    {
                        ...(pendientes && {estado: ['A']}),
                        ...(!filter?.fecha_creacion&&{
                            fecha_creacion: [
                                `${currentYear}-01-01 00:00:00`,
                                `${currentYear}-12-31 23:59:59`
                            ]
                        })
                    }, 
                    {in: ['estado'], between: ['fecha_creacion']}
                )
            }
            const response = await SolicitudService.indexSolicitudPost({filter: _filter}, {..._filters, tipo: 'FF'})
            if (response.status === 200) {
                setSolicitudes(response.data.data)
                setPagination(response.data.pagination)
            }
        }
        loadSolicitudes()
    }, [filters, pendientes])

    const openDialog = (name) => setShow({...show, [name]: true})
    const closeInfoDialog = () => setShow({...show, info: false})
    const closeDenyDialog = () => setShow({...show, deny: false})
    const closeVerifyDialog = () => setShow({...show, verify: false})

    const handleOpenDialog = (action, rowData) => {
        setSelected(rowData)
        if (action === 'verify') {
            openDialog('verify')
        } else if (action === 'deny') {
            openDialog('deny')
        } else if (action === 'view') {
            openDialog('info')
        }
    }

    const handleVerify = async () => {
        if (selected) {
            const response = await SolicitudService.verificar(selected.id)
            dispatch(setHttpMessage({status: response.status, title: response.data.message}))
            if (response.status === 200) {
                reloadFilters()
                closeVerifyDialog()
            }
        }
    }
    const handleDeny = async (values) => {
        if (selected) {
            const response = await SolicitudService.denegar(values, selected.id)
            dispatch(setHttpMessage({status: response.status, title: response.data.message}))
            if (response.status === 200) {
                reloadFilters()
                closeDenyDialog()
            }
        }
    }
    const cleanSelected = () => setSelected(null)

    const footerDenyDialog = <>
        <Button startIcon='pi pi-times' variant='text-secondary' onClick={closeDenyDialog}>Cerrar</Button>
        <Button startIcon='pi pi-save' variant='text-secondary' onClick={() => formRef?.current?.handleSubmit()}>Guardar</Button>
    </>
    const toolbarOptions = <div className='btn-group'>
        <Button variant='tab-red' active={!pendientes} onClick={() => setPendientes(false)}>Todos</Button>
        <Button variant='tab-red' active={pendientes} onClick={() => setPendientes(true)}>Pendientes</Button>
    </div>

    return <>
        <TitlePage title='Lista de Solicitudes' />
        <div className='content'>
            <div>
                <TableSolicitud 
                    tableRef={tableRef}
                    solicitudes={solicitudes}
                    pagination={pagination}
                    filterHandlers={filterHandlers}
                    handleOpenDialog={handleOpenDialog}
                    toolbarOptions={toolbarOptions}
                    dataTableProps={{
                        rowClassName: (rowData) => (colorsEstado[rowData.estado]),
                        tableClassName: '',
                        tableStyle: {borderColor: '#c7cace'}
                    }}
                />

                <Dialog
                    show={show.info}
                    header='Información'
                    footer={<Button startIcon='pi pi-times' variant='text-secondary' onClick={closeInfoDialog}>Cerrar</Button>}
                    onHide={closeInfoDialog}
                    style={{width: '60rem'}}
                    onExited={cleanSelected}
                    scrollable
                >
                    <ViewSolicitud id={selected?.id} />
                </Dialog>
                <Dialog
                    show={show.deny}
                    header='Registrar observaciones'
                    footer={footerDenyDialog}
                    onHide={closeDenyDialog}
                    style={{width: '30rem'}}
                    onExited={cleanSelected}
                >
                    <FormDeny formRef={formRef} onSubmit={handleDeny} selected={selected} />
                </Dialog>
                <ConfirmDialog 
                    show={show.verify}
                    onHide={closeVerifyDialog}
                    onReject={closeVerifyDialog}
                    onAccept={handleVerify}
                >
                    Esta seguro de <span className='text-primary'>VERIFICAR</span> la Solicitud {selected?.numero_solicitud}?
                </ConfirmDialog>
            </div>
        </div>
    </>
}
export default SolicitudDAFIndex